/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { StepProps, StepStatus } from '../../types';
import { StepperContext } from './context';
import StepIndicator from './StepIndicator';

// _index is injected by the Stepper parent via React.cloneElement — not part of the public API.
interface InternalStepProps extends StepProps {
  _index?: number;
}

const Step = ({ title, description, status, _index = 0 }: InternalStepProps) => {
  const { current, direction, totalSteps, labelBackground } = React.useContext(StepperContext);
  const resolvedStatus: StepStatus =
    status ?? (_index === current ? 'active' : _index < current ? 'completed' : 'inactive');
  const isLast = _index === totalSteps - 1;

  return (
    <div
      className={cx('stepper-step', `stepper-step--${resolvedStatus}`, `stepper-step--${direction}`, {
        'stepper-step--last': isLast,
      })}>
      <div className="stepper-step__track">
        <StepIndicator status={resolvedStatus} />
        {!isLast && <div className="stepper-step__connector" />}
      </div>
      <div
        className={cx('stepper-step__content', {
          'stepper-step__content--background': labelBackground && resolvedStatus === 'active',
        })}>
        <span className="stepper-step__title">{title}</span>
        {description && <span className="stepper-step__description">{description}</span>}
      </div>
    </div>
  );
};

Step.displayName = 'Stepper.Step';

export default Step;
